import { supabase } from './supabase';
import toast from 'react-hot-toast';
import {
  assertReceiptNotDuplicate,
  DuplicateReceiptError,
  findDuplicateReceipt,
} from './expenseReceiptDuplicateCheck';
import { receiveAllPurchaseOrderItems } from './purchaseOrders';

export interface ExpenseCategoryRef {
  id: string;
  name: string;
}

export interface TaxBreakdownRow {
  name: string;
  rate: string;
  amount: string;
}

export interface BackgroundReceiptJobInput {
  expenseId: string;
  businessId: string;
  file: File;
  categories: ExpenseCategoryRef[];
  bookingId?: string | null;
  projectId?: string | null;
  categoryId?: string | null;
  amount?: number | null;
  description?: string | null;
  merchantName?: string | null;
  expenseDate: string;
  paymentMethod?: string | null;
  taxBreakdown?: TaxBreakdownRow[];
  notes?: string | null;
  isTaxDeductible?: boolean;
  /** If user already ran AI in the modal, skip a second AI call. */
  manualAiData?: Record<string, unknown> | null;
  /** SHA-256 of file bytes; set after duplicate check. */
  fileHash?: string;
}

async function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      resolve(result.split(',')[1]);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

async function resolveBookingProjectIds(
  bookingId: string | null,
  projectId: string | null
): Promise<{ bookingId: string | null; projectId: string | null }> {
  let finalBookingId = bookingId;
  let finalProjectId = projectId;

  if (finalBookingId && !finalProjectId) {
    const { data: bookingData } = await supabase
      .from('bookings')
      .select('project_id')
      .eq('id', finalBookingId)
      .single();
    if (bookingData?.project_id) finalProjectId = bookingData.project_id;
  }

  if (finalProjectId && !finalBookingId) {
    const { data: projectData } = await supabase
      .from('projects')
      .select('booking_id')
      .eq('id', finalProjectId)
      .single();
    if (projectData?.booking_id) finalBookingId = projectData.booking_id;
  }

  if (finalBookingId && !finalProjectId) {
    const { data: projectWithBooking } = await supabase
      .from('projects')
      .select('id')
      .eq('booking_id', finalBookingId)
      .maybeSingle();
    if (projectWithBooking?.id) finalProjectId = projectWithBooking.id;
  }

  return { bookingId: finalBookingId, projectId: finalProjectId };
}

function parseTaxBreakdown(rows: TaxBreakdownRow[] | undefined) {
  const parsed = (rows ?? [])
    .filter((t) => t.name.trim() && parseFloat(t.amount) > 0)
    .map((t) => ({
      name: t.name.trim(),
      rate: t.rate ? parseFloat(t.rate) : null,
      amount: parseFloat(t.amount) || 0,
    }));
  const totalTax = parsed.reduce((sum, t) => sum + t.amount, 0);
  return { parsed, totalTax };
}

function findCategoryId(
  categories: ExpenseCategoryRef[],
  suggestedName: string | undefined
): string | null {
  if (!suggestedName) return null;
  const match = categories.find(
    (c) => c.name.toLowerCase() === suggestedName.toLowerCase()
  );
  if (match) return match.id;
  const other = categories.find((c) => c.name.toLowerCase() === 'other');
  return other?.id ?? null;
}

export function notifyExpensesUpdated() {
  window.dispatchEvent(new CustomEvent('expensesUpdated'));
}

/**
 * Normalize a PO reference for matching: uppercase, strip separators, and
 * drop leading zeros in digit runs so "P.O. 0012" matches "PO-12".
 */
function normalizePoNumber(value: string): string {
  return value
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .replace(/\d+/g, (m) => String(parseInt(m, 10)));
}

/**
 * Auto Match POs (expenses app setting, off by default).
 *
 * When a scanned receipt references one of our PO numbers:
 * - If the PO already has a bill expense, move the invoice image onto that
 *   expense and delete the duplicate expense the scan created.
 * - If the PO has no bill yet, convert the scanned expense INTO the bill
 *   (link + vendor) and auto-receive the PO's remaining items.
 *
 * Returns a user-facing success message, or null when no match applied.
 */
async function tryAutoMatchPurchaseOrder(params: {
  expenseId: string;
  businessId: string;
  poNumber: string | null;
  filePath: string;
  publicUrl: string;
}): Promise<string | null> {
  const { expenseId, businessId, poNumber, filePath, publicUrl } = params;
  if (!poNumber?.trim()) return null;

  const { data: enabled } = await supabase.rpc('get_app_setting', {
    p_business_id: businessId,
    p_app_slug: 'expenses',
    p_setting_key: 'auto_match_pos',
    p_default_value: false,
  });
  if (enabled !== true) return null;

  // Find the referenced PO (sent orders only — drafts were never sent to a
  // supplier and cancelled orders shouldn't attract bills).
  const { data: candidates } = await supabase
    .from('purchase_orders')
    .select('id, po_number, status, supplier_id, project_id, booking_id')
    .eq('business_id', businessId)
    .not('po_number', 'is', null)
    .not('status', 'in', '(draft,cancelled)')
    .order('created_at', { ascending: false })
    .limit(300);

  const target = normalizePoNumber(poNumber);
  const matches = (candidates ?? []).filter(
    (po) => po.po_number && normalizePoNumber(po.po_number) === target
  );
  // Zero matches: nothing to do. Multiple: ambiguous, leave it to the user.
  if (matches.length !== 1) return null;
  const po = matches[0];

  // Does this PO already have a bill expense (other than the scan itself)?
  const { data: existingBills } = await supabase
    .from('expenses')
    .select('id, receipt_image_url')
    .eq('purchase_order_id', po.id)
    .neq('id', expenseId)
    .order('created_at', { ascending: false })
    .limit(1);
  const bill = existingBills?.[0] ?? null;

  if (bill) {
    // Attach the invoice to the existing bill and drop the duplicate expense.
    await supabase
      .from('expense_receipts')
      .update({ expense_id: bill.id, updated_at: new Date().toISOString() })
      .eq('expense_id', expenseId);

    if (!bill.receipt_image_url) {
      await supabase
        .from('expenses')
        .update({
          receipt_image_path: filePath,
          receipt_image_url: publicUrl,
          updated_at: new Date().toISOString(),
        })
        .eq('id', bill.id);
    }

    await supabase.from('expenses').delete().eq('id', expenseId);
    return `Invoice matched to ${po.po_number} and attached to its expense`;
  }

  // No bill yet: this scanned expense BECOMES the bill.
  const { error: linkError } = await supabase
    .from('expenses')
    .update({
      purchase_order_id: po.id,
      vendor_id: po.supplier_id ?? null,
      ...(po.project_id ? { project_id: po.project_id } : {}),
      ...(po.booking_id ? { booking_id: po.booking_id } : {}),
      updated_at: new Date().toISOString(),
    })
    .eq('id', expenseId);
  if (linkError) throw linkError;

  // Auto-receive the remaining items (invoice in hand means goods shipped).
  let received = false;
  try {
    const receiptId = await receiveAllPurchaseOrderItems(
      po.id,
      'Auto-received from scanned supplier invoice'
    );
    received = receiptId !== null;
  } catch (err) {
    console.error('Auto-receive after PO match failed:', err);
  }

  return received
    ? `Invoice matched to ${po.po_number} — bill created and items received`
    : `Invoice matched to ${po.po_number} — bill created`;
}

/** Creates a placeholder expense + processing receipt row before the modal closes. */
export async function createProcessingExpenseRecord(
  input: BackgroundReceiptJobInput
): Promise<void> {
  const fileHash =
    input.fileHash ?? (await assertReceiptNotDuplicate(input.businessId, input.file));

  const { data: { user } } = await supabase.auth.getUser();
  const { bookingId, projectId } = await resolveBookingProjectIds(
    input.bookingId ?? null,
    input.projectId ?? null
  );

  const hasManual = Boolean(input.amount && input.description);
  const placeholderDescription =
    input.description?.trim() || 'Processing receipt…';
  const placeholderAmount = input.amount ?? 0;

  const { error: expenseError } = await supabase.from('expenses').insert({
    id: input.expenseId,
    business_id: input.businessId,
    booking_id: bookingId,
    project_id: projectId,
    category_id: input.categoryId || null,
    amount: placeholderAmount,
    currency: 'USD',
    description: placeholderDescription,
    merchant_name: input.merchantName?.trim() || null,
    expense_date: input.expenseDate,
    payment_method: input.paymentMethod || null,
    tax_amount: 0,
    tax_breakdown: [],
    is_tax_deductible: input.isTaxDeductible ?? true,
    receipt_image_path: null,
    receipt_image_url: null,
    notes: input.notes?.trim() || null,
    created_by: user?.id || null,
    ai_categorized: !!input.manualAiData,
    ai_category_suggestion: (input.manualAiData as { suggestedCategory?: string })
      ?.suggestedCategory ?? null,
    ai_confidence_score: (input.manualAiData as { confidenceScore?: number })
      ?.confidenceScore ?? null,
    ai_extracted_data: input.manualAiData || null,
    approval_status: 'approved',
  });

  if (expenseError) throw expenseError;

  const { error: receiptError } = await supabase.from('expense_receipts').insert({
    expense_id: input.expenseId,
    business_id: input.businessId,
    file_name: input.file.name,
    file_path: '',
    file_url: '',
    file_size: input.file.size,
    mime_type: input.file.type,
    file_hash: fileHash,
    ai_processed: false,
    processing_status: 'processing',
  });

  if (receiptError) {
    if (receiptError.code === '23505') {
      const match = await findDuplicateReceipt(input.businessId, fileHash);
      if (match) throw new DuplicateReceiptError(match);
    }
    throw receiptError;
  }

  if (hasManual && input.manualAiData) {
    // User filled form + had AI data — still run background upload only
    return;
  }
}

async function markReceiptFailed(expenseId: string, message: string) {
  await supabase
    .from('expense_receipts')
    .update({ processing_status: 'failed', updated_at: new Date().toISOString() })
    .eq('expense_id', expenseId);

  await supabase
    .from('expenses')
    .update({
      description: 'Receipt processing failed',
      notes: message,
      updated_at: new Date().toISOString(),
    })
    .eq('id', expenseId);
}

/** Upload receipt, run AI, and finalize the expense (fire-and-forget). */
export async function runReceiptBackgroundJob(
  input: BackgroundReceiptJobInput
): Promise<void> {
  const { expenseId, businessId, file, categories } = input;

  try {
    const fileExt = file.name.split('.').pop();
    const fileName = `${Date.now()}_${Math.random().toString(36).substring(7)}.${fileExt}`;
    const filePath = `expense-receipts/${businessId}/${expenseId}/${fileName}`;

    const { error: uploadError } = await supabase.storage
      .from('user-uploads')
      .upload(filePath, file);

    if (uploadError) throw uploadError;

    const { data: { publicUrl } } = supabase.storage
      .from('user-uploads')
      .getPublicUrl(filePath);

    await supabase
      .from('expenses')
      .update({
        receipt_image_path: filePath,
        receipt_image_url: publicUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('id', expenseId);

    await supabase
      .from('expense_receipts')
      .update({
        file_path: filePath,
        file_url: publicUrl,
        updated_at: new Date().toISOString(),
      })
      .eq('expense_id', expenseId);

    let aiData: Record<string, unknown> | null = input.manualAiData ?? null;

    if (!aiData) {
      const imageBase64 = await fileToBase64(file);
      const { data, error } = await supabase.functions.invoke('ai-categorize-expense', {
        body: {
          businessId,
          imageBase64,
          imageMimeType: file.type,
          existingCategories: categories.map((c) => c.name),
          autoCreateExpense: false,
        },
      });

      if (error) throw error;

      if (data?.insufficientTokens) {
        await markReceiptFailed(
          expenseId,
          'Insufficient AI tokens to categorize this receipt.'
        );
        toast.error('Receipt saved but AI needs more tokens to categorize.');
        notifyExpensesUpdated();
        return;
      }

      if (!data?.success || !data?.data) {
        throw new Error(data?.error || 'Failed to extract expense data');
      }

      aiData = data.data as Record<string, unknown>;
    }

    await finalizeAiReceipt({
      expenseId,
      businessId,
      categories,
      aiData,
      filePath,
      publicUrl,
      bookingId: input.bookingId,
      projectId: input.projectId,
      categoryId: input.categoryId,
      amount: input.amount,
      description: input.description,
      merchantName: input.merchantName,
      expenseDate: input.expenseDate,
      paymentMethod: input.paymentMethod,
      taxBreakdown: input.taxBreakdown,
      notes: input.notes,
      isTaxDeductible: input.isTaxDeductible,
    });
  } catch (err: unknown) {
    const message =
      err instanceof Error ? err.message : 'Failed to process receipt';
    console.error('Background receipt processing failed:', err);
    await markReceiptFailed(expenseId, message);
    toast.error(message);
    notifyExpensesUpdated();
  }
}

async function finalizeAiReceipt(input: {
  expenseId: string;
  businessId: string;
  categories: ExpenseCategoryRef[];
  aiData: Record<string, unknown>;
  filePath: string;
  publicUrl: string;
  bookingId?: string | null;
  projectId?: string | null;
  categoryId?: string | null;
  amount?: number | null;
  description?: string | null;
  merchantName?: string | null;
  expenseDate: string;
  paymentMethod?: string | null;
  taxBreakdown?: TaxBreakdownRow[];
  notes?: string | null;
  isTaxDeductible?: boolean;
}): Promise<void> {
  const { expenseId, businessId, categories, aiData, filePath, publicUrl } = input;
  const amount =
      typeof aiData.amount === 'number'
        ? aiData.amount
        : parseFloat(String(aiData.amount ?? input.amount ?? 0));

    let taxBreakdown: { name: string; rate: number | null; amount: number }[] = [];
    if (Array.isArray(aiData.taxBreakdown) && aiData.taxBreakdown.length > 0) {
      taxBreakdown = (aiData.taxBreakdown as Array<{
        name?: string;
        rate?: number | string;
        amount?: number | string;
      }>).map((t) => ({
        name: t.name || 'Tax',
        rate: t.rate != null ? Number(t.rate) : null,
        amount: parseFloat(String(t.amount || 0)) || 0,
      }));
    } else if (input.taxBreakdown?.length) {
      taxBreakdown = parseTaxBreakdown(input.taxBreakdown).parsed;
    }

    const totalTax = taxBreakdown.reduce((sum, t) => sum + t.amount, 0);
    const categoryId =
      input.categoryId ||
      findCategoryId(categories, aiData.suggestedCategory as string | undefined);

    const { bookingId, projectId } = await resolveBookingProjectIds(
      input.bookingId ?? null,
      input.projectId ?? null
    );

    const { error: updateError } = await supabase
      .from('expenses')
      .update({
        booking_id: bookingId,
        project_id: projectId,
        category_id: categoryId,
        amount: Number.isFinite(amount) ? amount : 0,
        description: String(aiData.description || input.description || 'Expense'),
        merchant_name: String(aiData.merchantName || input.merchantName || '') || null,
        expense_date: String(aiData.expenseDate || input.expenseDate),
        payment_method: String(
          aiData.paymentMethod || input.paymentMethod || ''
        ).replace(/\s+/g, '_') || null,
        tax_amount: totalTax,
        tax_breakdown: taxBreakdown,
        is_tax_deductible: input.isTaxDeductible ?? true,
        notes: input.notes?.trim() || null,
        ai_categorized: true,
        ai_category_suggestion: (aiData.suggestedCategory as string) || null,
        ai_confidence_score: (aiData.confidenceScore as number) ?? null,
        ai_extracted_data: aiData,
        approval_status: 'pending',
        approved_at: null,
        approved_by: null,
        updated_at: new Date().toISOString(),
      })
      .eq('id', expenseId);

    if (updateError) throw updateError;

    await supabase
      .from('expense_receipts')
      .update({
        ai_processed: true,
        processing_status: 'completed',
        updated_at: new Date().toISOString(),
      })
      .eq('expense_id', expenseId);

    // Auto Match POs: a failure here must not fail the receipt itself.
    let matchMessage: string | null = null;
    try {
      matchMessage = await tryAutoMatchPurchaseOrder({
        expenseId,
        businessId,
        poNumber: (aiData.poNumber as string | undefined) ?? null,
        filePath,
        publicUrl,
      });
    } catch (err) {
      console.error('Auto Match POs failed:', err);
    }

  toast.success(matchMessage ?? 'Receipt categorized — review and approve on Expenses');
  notifyExpensesUpdated();
}

/**
 * Run AI again on a receipt that already uploaded but failed to process.
 * Uses the file already in storage — nothing is uploaded a second time.
 */
export async function reprocessExpenseReceipt(params: {
  expenseId: string;
  businessId: string;
  categories: ExpenseCategoryRef[];
}): Promise<void> {
  const { expenseId, businessId, categories } = params;

  const { data: receipt, error: receiptError } = await supabase
    .from('expense_receipts')
    .select('file_path, file_url, mime_type, file_name')
    .eq('expense_id', expenseId)
    .order('created_at', { ascending: false })
    .limit(1)
    .maybeSingle();
  if (receiptError) throw receiptError;
  if (!receipt?.file_path) {
    throw new Error('This receipt file was not saved, so it can’t be reprocessed.');
  }

  const { data: expense, error: expenseError } = await supabase
    .from('expenses')
    .select('booking_id, project_id, category_id, expense_date, is_tax_deductible, payment_method')
    .eq('id', expenseId)
    .single();
  if (expenseError) throw expenseError;

  const now = new Date().toISOString();
  await supabase
    .from('expense_receipts')
    .update({ processing_status: 'processing', ai_processed: false, updated_at: now })
    .eq('expense_id', expenseId);
  await supabase
    .from('expenses')
    .update({ description: 'Processing receipt…', notes: null, updated_at: now })
    .eq('id', expenseId);
  notifyExpensesUpdated();

  try {
    const { data: blob, error: downloadError } = await supabase.storage
      .from('user-uploads')
      .download(receipt.file_path);
    if (downloadError || !blob) throw downloadError || new Error('Could not load the receipt file');

    const file = new File([blob], receipt.file_name || 'receipt', {
      type: receipt.mime_type || blob.type || 'image/jpeg',
    });
    const imageBase64 = await fileToBase64(file);
    const { data, error } = await supabase.functions.invoke('ai-categorize-expense', {
      body: {
        businessId,
        imageBase64,
        imageMimeType: file.type,
        existingCategories: categories.map((c) => c.name),
        autoCreateExpense: false,
      },
    });
    if (error) throw error;
    if (data?.insufficientTokens) {
      await markReceiptFailed(expenseId, 'Insufficient AI tokens to categorize this receipt.');
      toast.error('Receipt saved but AI needs more tokens to categorize.');
      notifyExpensesUpdated();
      return;
    }
    if (!data?.success || !data?.data) {
      throw new Error(data?.error || 'Failed to extract expense data');
    }

    const { data: { publicUrl } } = supabase.storage.from('user-uploads').getPublicUrl(receipt.file_path);
    await finalizeAiReceipt({
      expenseId,
      businessId,
      categories,
      aiData: data.data as Record<string, unknown>,
      filePath: receipt.file_path,
      publicUrl: receipt.file_url || publicUrl,
      bookingId: expense.booking_id,
      projectId: expense.project_id,
      categoryId: expense.category_id,
      expenseDate: expense.expense_date,
      paymentMethod: expense.payment_method,
      isTaxDeductible: expense.is_tax_deductible,
    });
  } catch (err: unknown) {
    const message = err instanceof Error ? err.message : 'Failed to process receipt';
    console.error('Reprocess receipt failed:', err);
    await markReceiptFailed(expenseId, message);
    toast.error(message);
    notifyExpensesUpdated();
  }
}
